import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getCurrentAlgoliaInsightsUserToken } from 'lib/algolia'
import { reportSentryError } from 'lib/sentry'
import { CustomerTypes } from 'modules/customer'
import {
  getMultipleProductsByIds,
  GetMultipleProductsByIdsResponse
} from 'modules/products'
import { QueryKey } from 'shared/api'
import { distributionCenterId } from 'shared/config/distributionCenter'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { searchProductsSeo } from '../analytics'
import { getSearchPageResultsIDs } from '../api'

export interface UseQuerySearchProductsProps {
  customer?: CustomerTypes
  prefetchedProductIds: string[]
  prefetchedQuery?: string
  prefetchedQueryID?: string | null
  clearPrefetchedResultsData: () => void | Promise<void>
  token?: string
  query?: string
  limit?: number
}

export const useQuerySearchProducts = ({
  customer,
  prefetchedProductIds,
  prefetchedQuery,
  prefetchedQueryID,
  clearPrefetchedResultsData,
  token,
  query,
  limit = PRODUCTS_LISTING_LIMIT
}: UseQuerySearchProductsProps) => {
  const latestSearchRequestIdRef = useRef(0)

  const [productIds, setProductIds] = useState<string[]>([])
  const [queryID, setQueryID] = useState<string | null>(null)
  const [productIdsLoadCompleted, setProductIdsLoadCompleted] = useState(false)

  const loadFallbackProductIds = useCallback(
    async (searchQuery: string) => {
      const requestId = ++latestSearchRequestIdRef.current
      const userToken = await getCurrentAlgoliaInsightsUserToken(customer?.id)

      try {
        const { ids, meta } = await getSearchPageResultsIDs(
          searchQuery,
          userToken
        )

        if (requestId !== latestSearchRequestIdRef.current) return

        setProductIds(ids ?? [])
        setQueryID(meta.queryID ?? null)

        searchProductsSeo({
          customer,
          searchQuery
        })
      } catch (err) {
        if (requestId !== latestSearchRequestIdRef.current) return
        reportSentryError(err, 'Failed to get search page results IDs')
        setProductIds([])
        setQueryID(null)
      } finally {
        if (requestId === latestSearchRequestIdRef.current) {
          setProductIdsLoadCompleted(true)
        }
      }
    },
    [customer]
  )

  useEffect(() => {
    if (!query) return

    setProductIdsLoadCompleted(false)

    // Clear stale ids immediately to avoid running products-by-ids query
    // with results from the previous search query.
    setProductIds([])
    setQueryID(null)

    // Clear stale prefetched data.
    if (prefetchedQuery && prefetchedQuery !== query) {
      void clearPrefetchedResultsData()
    }

    const hasPrefetchedData =
      prefetchedQuery === query && !isNilOrEmpty(prefetchedProductIds)

    if (hasPrefetchedData) {
      setProductIds(prefetchedProductIds)
      setQueryID(prefetchedQueryID ?? null)
      setProductIdsLoadCompleted(true)
    } else {
      void loadFallbackProductIds(query)
    }
    // prefetchedProductIds is excluded from the dependency array intentionlly.
    // This blocks reloading during using search dropdown on search page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, loadFallbackProductIds])

  const productsQuery = useQuery<GetMultipleProductsByIdsResponse>({
    queryKey: [
      QueryKey.searchProducts,
      token,
      JSON.stringify(productIds),
      query,
      distributionCenterId
    ],
    queryFn: async () => {
      // Send the full id set; the backend resolves only the first page of
      // details, further pages are handled by the infinite-scroll fetcher
      // (getNextMultipleProductsByIdsPage).
      const response = await getMultipleProductsByIds({
        productIds,
        token,
        distributionCenterId,
        query: { limit: limit.toString() }
      })

      return response
    },
    enabled: !isNilOrEmpty(productIds) && !isNilOrEmpty(query)
  })

  const hasResolvedResults = useMemo(() => {
    return (
      (productIds.length > 0 &&
        productsQuery.data?.data != null &&
        !productsQuery.isError) ||
      (productIds.length === 0 && productIdsLoadCompleted)
    )
  }, [
    productIds,
    productIdsLoadCompleted,
    productsQuery.data?.data,
    productsQuery.isError
  ])

  const hasFailedToFetchProductsDetails =
    productIdsLoadCompleted && productIds.length > 0 && productsQuery.isError

  return {
    ...productsQuery,
    productIds,
    queryID,
    hasResolvedResults,
    hasFailedToFetchProductsDetails
  }
}
